import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Row, Col, Button, Table } from 'react-bootstrap'
import Message from '../../shared_components/Message'
import ToastMessage from '../components/ToastMessage'
import Loader from '../../shared_components/Loader'

import {
  getJitaiMessageList,
  deleteJitaiMessage,
} from '../actions/jitaiActions'
import {
  JITAI_MESSAGE_DELETE_RESET,
  JITAI_MESSAGE_LIST_RESET,
  JITAI_MESSAGE_CREATE_RESET,
  JITAI_MESSAGE_UPDATE_RESET,
} from '../constants/jitaiConstants'

import resetErrorState from '../utils/resetErrorState'

import hasValidLoginCredential from '../../admin_module/utils/hasValidLoginCredential'
import { getAllAccess } from '../../access_module/actions/accessActions'
import { GET_ALL_ACCESS_RESET } from '../../access_module/constants/accessConstants'
import { listStudyDetails } from '../../study_module/actions/studyActions'
import { STUDY_DETAILS_RESET } from '../../study_module/constants/studyConstants'
import UpsertJitaiMessage from '../components/UpsertJitaiMessage'

const JitaiMessagesScreen = ({ history, match }) => {
  const studyId = match.params.studyId

  // For toast message
  const [toastMessages, setToastMessages] = useState([])

  const toastOnCloseHandler = async (uid) => {
    setToastMessages((toastMessages) =>
      toastMessages.filter((message) => message.uid !== uid)
    )
  }

  const dispatch = useDispatch()

  //It is needed to check if the logged in user is an admin
  const userLogin = useSelector((state) => state.userLogin)
  const { userInfo, logout } = userLogin

  const jitaiMessageList = useSelector((state) => state.jitaiMessageList)
  const {
    jitaiMessages,
    error: errorGetList,
    loading: loadingGetList,
    success: successGetList,
  } = jitaiMessageList

  const jitaiMessageDelete = useSelector((state) => state.jitaiMessageDelete)
  const {
    loading: loadingDelete,
    error: errorDelete,
    success: successDelete,
  } = jitaiMessageDelete

  const jitaiMessageCreate = useSelector((state) => state.jitaiMessageCreate)
  const { success: successCreate } = jitaiMessageCreate

  const jitaiMessageUpdate = useSelector((state) => state.jitaiMessageUpdate)
  const { success: successUpdate } = jitaiMessageUpdate

  const allAccess = useSelector((state) => state.allAccess)
  const {
    loading: loadingGetAllAccess,
    error: errorGetAllAccess,
    success: successGetAllAccess,
    allAccessList,
  } = allAccess

  const studyDetails = useSelector((state) => state.studyDetails)
  const {
    loading: loadingStudy,
    error: errorStudy,
    study: loadedStudy,
    success: successStudy,
  } = studyDetails

  useEffect(() => {
    if (!hasValidLoginCredential(userInfo, logout, window)) {
      dispatch({ type: JITAI_MESSAGE_LIST_RESET })
      history.push('/login')
    } else {
      if (!loadingGetList && !successGetList && !errorGetList) {
        resetErrorState(dispatch)
        // Put the reset for getting all access just before loading the main content again
        dispatch({ type: GET_ALL_ACCESS_RESET })
        dispatch(getJitaiMessageList(studyId))
      }

      if (!loadingGetAllAccess && !successGetAllAccess && !errorGetAllAccess) {
        dispatch(getAllAccess())
      }

      if (!loadingStudy && !errorStudy && !successStudy) {
        dispatch(listStudyDetails(studyId))
      }

      if (successDelete) {
        dispatch({ type: JITAI_MESSAGE_DELETE_RESET })
        dispatch(getJitaiMessageList(studyId))
      }

      if (successCreate) {
        dispatch(getJitaiMessageList(studyId))
      }

      if (successUpdate) {
        dispatch(getJitaiMessageList(studyId))
      }
    }
  }, [
    dispatch,
    history,
    userInfo,
    studyId,
    logout,
    loadingGetList,
    loadingGetAllAccess,
    loadingStudy,
    errorGetList,
    errorGetAllAccess,
    errorStudy,
    successDelete,
    successCreate,
    successUpdate,
    successGetList,
    successGetAllAccess,
    successStudy,
  ])

  const deleteJitaiMessageHandler = (jitaiMessage) => {
    if (window.confirm('Are you sure you want to delete this jitai message?')) {
      resetErrorState(dispatch)
      dispatch(deleteJitaiMessage(studyId, jitaiMessage._id))
    }
  }

  const headingStyle = {
    fontSize: '2rem',
    fontWeight: 'bold',
  }

  return (
    <>
      {/* Display toast messages */}
      <div
        style={{
          position: 'absolute',
          backgroundColor: 'transparent',
          pointerEvents: 'none',
          height: '80vh',
          width: '80vw',
          zIndex: 1,
        }}
      >
        {toastMessages.map((toastMessage) => (
          <ToastMessage
            key={toastMessage.uid}
            variant={toastMessage.variant}
            onClose={() => toastOnCloseHandler(toastMessage.uid)}
          >
            {toastMessage.message}
          </ToastMessage>
        ))}
      </div>

      <Row className='align-items-center'>
        <Col>
          <h1 style={headingStyle}>Jitai Messages</h1>
          {loadedStudy && (
            <p className='mb-3'>
              <strong>Study:</strong> {loadedStudy.chin_name} ({loadedStudy.study_code})
            </p>
          )}
        </Col>
        <UpsertJitaiMessage
          studyId={studyId}
          jitaiMessageList={jitaiMessages}
          updatedJitaiMessage={undefined}
        ></UpsertJitaiMessage>
      </Row>

      {errorDelete && <Message variant='danger'>{errorDelete}</Message>}
      {loadingGetList || loadingDelete ? (
        <Loader />
      ) : errorGetList ? (
        <Message variant='danger'>{errorGetList}</Message>
      ) : (
        <>
          <p>
            Manage jitai messages for this study. These messages can be used for
            just-in-time adaptive interventions.
          </p>
          <Table striped bordered hover responsive className='table-sm'>
            <thead>
              <tr>
                <th>Code</th>
                <th>Created At</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {!logout &&
                jitaiMessages.map((jitaiMessage) => (
                  <tr key={jitaiMessage._id}>
                    <td className='align-middle'>{jitaiMessage.code}</td>
                    <td className='align-middle'>
                      {new Date(jitaiMessage.createdAt).toLocaleDateString()}
                    </td>
                    <td className='align-middle'>
                      <UpsertJitaiMessage
                        studyId={studyId}
                        jitaiMessageList={jitaiMessages}
                        updatedJitaiMessage={jitaiMessage}
                      ></UpsertJitaiMessage>

                      <Button
                        variant='danger'
                        className='btn-sm mx-1'
                        onClick={() =>
                          deleteJitaiMessageHandler(jitaiMessage)
                        }
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </Table>
        </>
      )}
    </>
  )
}

export default JitaiMessagesScreen 